import React from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Button,
  Stack,
} from "@mui/material";
import {
  Invite,
  useGetPropertyNameByIdQuery,
} from "../../redux/residentPortalApi";
import { isSameDay, isWithinInterval } from "date-fns";
import config from "../../config";
import { isOverstaying } from "../../utils/visitStatus";
import VisitLogTimeline from "./VisitLogTimeline";

interface OwnProps {
  showCard?: boolean;
  showBanner?: boolean;
  showDownloadBtn?: boolean;
  showAddAnotherInviteBtn?: boolean;
  showVisitLog?: boolean;
  invite: Invite | null;
  addAnotherInviteCallback?: () => void;
  captureId?: boolean;
}

const InviteDetails: React.FC<OwnProps> = (props) => {
  const {
    showCard = true,
    showBanner = true,
    showDownloadBtn = false,
    showAddAnotherInviteBtn = false,
    showVisitLog = false,
    invite,
    addAnotherInviteCallback,
  } = props;

  const { data: propertyName } = useGetPropertyNameByIdQuery(
    { id: invite?.propertyId || "" },
    { skip: !invite?.propertyId },
  );

  if (!invite) {
    return (
      <Card raised={showCard} sx={{ borderRadius: "20px", margin: 2 }}>
        <CardContent>
          <Typography variant="body1" style={{ textAlign: "center" }}>
            No invite data available.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  let isDateValid = false;
  let formattedMeetingDateTime = "";
  let formattedDateRange = "";
  const currentStatus =
    invite.currentStatus ?? (invite.scanned ? "checked_in" : "not_arrived");

  if (invite.isMultiDay && invite.startDate && invite.endDate) {
    // Handle multi-day invite
    const startDate = new Date(invite.startDate);
    const endDate = new Date(invite.endDate);

    const formatDate = (date: Date) =>
      date.toLocaleString("en-GB", {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
      });

    formattedDateRange = `${formatDate(startDate)} to ${formatDate(endDate)}`;

    const now = new Date();
    isDateValid = isWithinInterval(now, {
      start: new Date(startDate.setHours(0, 0, 0, 0)),
      end: new Date(endDate.setHours(23, 59, 59, 999)),
    });
  } else if (invite.visitDateTime) {
    // Handle single-day invite
    const visitDateTime = new Date(invite.visitDateTime);
    formattedMeetingDateTime = visitDateTime.toLocaleString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    });

    const now = new Date();
    const isToday = isSameDay(visitDateTime, now);
    // Valid from 2 hours before the meeting time until the end of that day
    const isValidTime = isWithinInterval(now, {
      start: new Date(visitDateTime.getTime() - 2 * 60 * 60 * 1000),
      end: new Date(new Date(visitDateTime).setHours(23, 59, 59, 999)),
    });

    isDateValid = isToday && isValidTime;
  }

  const getBannerDetails = () => {
    if (currentStatus === "checked_in" && isOverstaying(invite)) {
      return {
        backgroundColor: "#e65100",
        text: "Visitor is CHECKED IN and has overstayed the expected visit window",
      };
    }

    if (currentStatus === "checked_in") {
      return {
        backgroundColor: "#2e7d32",
        text: "Visitor is currently CHECKED IN",
      };
    }

    if (currentStatus === "checked_out") {
      return { backgroundColor: "#616161", text: "Visitor has CHECKED OUT" };
    }

    if (isDateValid) {
      if (invite.isMultiDay) {
        return {
          backgroundColor: "green",
          text: "Multi-day invite is valid for entry",
        };
      } else {
        return { backgroundColor: "green", text: "Invite is valid for entry" };
      }
    }

    if (invite.isMultiDay) {
      return {
        backgroundColor: "red",
        text: "Multi-day invite is not valid for entry today",
      };
    }

    return { backgroundColor: "red", text: "Invite is not valid for entry" };
  };

  const bannerDetails = getBannerDetails();

  return (
    <React.Fragment>
      {showBanner && (
        <Box
          sx={{
            backgroundColor: bannerDetails.backgroundColor,
            color: "white",
            padding: 1,
            textAlign: "center",
            marginBottom: 2,
          }}
        >
          <Typography sx={{ color: "white" }}>{bannerDetails.text}</Typography>
        </Box>
      )}
      <List>
        <ListItem>
          <ListItemText
            primary="Visitor Name"
            secondary={invite.visitorName || "N/A"}
          />
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText
            primary="Visitor Type"
            secondary={
              invite.visitorType ? invite.visitorType.toUpperCase() : "VISITOR"
            }
          />
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText primary="Purpose" secondary={invite.purpose || "N/A"} />
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText
            primary="Visitor Email"
            secondary={invite.visitorEmail || "N/A"}
          />
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText
            primary="Visitor WhatsApp"
            secondary={invite.visitorWhatsApp || "N/A"}
          />
        </ListItem>
        <Divider component="li" />

        {propertyName && (
          <ListItem>
            <ListItemText
              primary="Property Details"
              secondary={propertyName.propertyName}
            />
          </ListItem>
        )}
        <Divider component="li" />
        {invite.isMultiDay ? (
          <ListItem>
            <ListItemText
              primary="Visit Date Range"
              secondary={formattedDateRange || "N/A"}
            />
          </ListItem>
        ) : (
          <ListItem>
            <ListItemText
              primary="Meeting Date & Time"
              secondary={formattedMeetingDateTime || "N/A"}
            />
          </ListItem>
        )}
        <Divider component="li" />
        <ListItem>
          <ListItemText
            primary="Visit Status"
            secondary={
              currentStatus === "checked_in"
                ? `Checked in${
                    invite.lastScannedAt
                      ? ` at ${new Date(invite.lastScannedAt).toLocaleString(
                          "en-GB",
                        )}`
                      : ""
                  }`
                : currentStatus === "checked_out"
                ? `Checked out${
                    invite.lastScannedAt
                      ? ` at ${new Date(invite.lastScannedAt).toLocaleString(
                          "en-GB",
                        )}`
                      : ""
                  }`
                : "Not checked in yet"
            }
          />
        </ListItem>
      </List>
      {showVisitLog && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="subtitle2" color="#345678" gutterBottom>
            Check-In / Check-Out History
          </Typography>
          <VisitLogTimeline inviteId={invite.id} />
        </Box>
      )}
      <Stack direction={"row"} spacing={4}>
        {showAddAnotherInviteBtn && addAnotherInviteCallback && (
          <Button
            variant="text"
            color="primary"
            onClick={() => addAnotherInviteCallback()}
          >
            Create Another
          </Button>
        )}
        {showDownloadBtn && (
          <Button variant="contained" color="primary">
            <a
              href={`${config.CDN_URL}/invites/${invite.id}.png`}
              target="_blank"
              rel="noreferrer"
              download
              style={{ textDecoration: "none", color: "inherit" }}
            >
              Download Pass
            </a>
          </Button>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default InviteDetails;
